import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const clearStoredAuth = () => {
  sessionStorage.removeItem('mfu_token');
  sessionStorage.removeItem('mfu_user');
  sessionStorage.removeItem('mfu_login_date');
  sessionStorage.removeItem('mfu_otp_verified');
};

const isPageReload = () => {
  const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
  if (navigationEntry) return navigationEntry.type === 'reload';

  // Fallback for older browsers that do not support Navigation Timing Level 2.
  return performance.navigation?.type === 1;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  // ⚠️ สลับระหว่าง localhost และ production
  // 🔹 สำหรับ localhost: uncomment บรรทัดที่ 1, comment บรรทัดที่ 2
  // 🔹 สำหรับ production: comment บรรทัดที่ 1, uncomment บรรทัดที่ 2
 // const API_BASE_URL = 'http://localhost:8000';                                    // [1] localhost
  const API_BASE_URL = 'https://mfuwellnesscenter-server.dietz.asia';           // [2] production

  // Initialize auth state from this tab's sessionStorage.
  // Authentication must not be shared with a newly opened tab.
  useEffect(() => {
    const initAuth = async () => {
      // Remove credentials persisted by older builds. They must never be used
      // to authenticate a different tab.
      localStorage.removeItem('mfu_token');
      localStorage.removeItem('mfu_user');
      localStorage.removeItem('mfu_login_date');

      // Browsers can copy sessionStorage when a tab is duplicated. Only a real
      // reload may restore this tab's authenticated or pending OTP session.
      if (!isPageReload()) {
        clearStoredAuth();
        sessionStorage.removeItem('otp_pending');
        sessionStorage.removeItem('otp_verification_token');
      }

      const storedToken = sessionStorage.getItem('mfu_token');
      const storedUser = sessionStorage.getItem('mfu_user');
      const storedLoginDate = sessionStorage.getItem('mfu_login_date');
      const otpVerified = sessionStorage.getItem('mfu_otp_verified') === 'true';
      const today = new Date().toDateString();

      // ถ้าวันที่ login ไม่ตรงกับวันนี้ ให้ clear session และบังคับ login ใหม่
      if (storedToken && (storedLoginDate !== today || !otpVerified)) {
        console.log('🔒 Session expired or OTP not verified, forcing re-login');
        clearStoredAuth();
        setLoading(false);
        return;
      }

      if (storedToken && storedUser && otpVerified) {
        try {
          const parsedUser = JSON.parse(storedUser);

          setToken(storedToken);
          setUser(parsedUser);
          setLoading(false);

          // เริ่ม verify token กับ backend แบบ background
          setIsVerifying(true);

          try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
              token: storedToken
            }, {
              timeout: 10000
            });

            if (response.data.status === 'success' && response.data.data) {
              const userData = response.data.data.user;

              // ตรวจสอบว่า user มี role 'admin_doctor' หรือไม่
              const userRoles = Array.isArray(userData.role) ? userData.role : [userData.role];
              const hasAdminRole = userRoles.includes('admin_doctor');

              if (hasAdminRole) {
                setUser(userData);
                sessionStorage.setItem('mfu_user', JSON.stringify(userData));
                console.log('✅ Token verified (Admin Doctor), user role:', userData.role);
              } else {
                console.warn('⚠️ User does not have admin_doctor role:', userRoles);
                clearStoredAuth();
                setToken(null);
                setUser(null);
              }
            } else {
              console.warn('⚠️ Token invalid from backend response');
              clearStoredAuth();
              setToken(null);
              setUser(null);
            }
          } catch (verifyError) {
            if (verifyError.code === 'ECONNABORTED' || verifyError.message?.includes('timeout')) {
              // Network timeout - ไม่ล้าง localStorage, ให้ใช้ข้อมูลเดิม
              console.warn('⚠️ Token verification timeout, using cached user data');
            } else if (verifyError.response?.status === 401 || verifyError.response?.status === 403) {
              // Backend ตอบกลับว่า token ไม่ valid - ล้าง session ของแท็บนี้
              console.error('❌ Token unauthorized/forbidden, clearing session');
              clearStoredAuth();
              setToken(null);
              setUser(null);
            } else {
              // Network error อื่นๆ - ไม่ล้าง localStorage, ให้ใช้ข้อมูลเดิม
              console.warn('⚠️ Token verification network error, using cached user data:', verifyError.message);
            }
          } finally {
            setIsVerifying(false);
          }
        } catch (parseError) {
          console.error('❌ Error parsing stored user data:', parseError);
          clearStoredAuth();
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login with username and password
  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });

      if (response.data.status === 'success') {
        const { user: userData, token: tokenData } = response.data.data;

        // Username/password is only the pre-authentication step. Do not create
        // an authenticated session until the OTP page confirms the code.
        clearStoredAuth();
        setUser(null);
        setToken(null);

        console.log('✅ Password verified (Admin), waiting for OTP');
        return { success: true, user: userData, token: tokenData };
      } else {
        return { success: false, message: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
      };
    }
  };

  // Promote a password-verified login to an authenticated tab session only
  // after OTP verification succeeds.
  const completeOtpLogin = (userData, tokenData) => {
    if (!userData || !tokenData) return false;

    setUser(userData);
    setToken(tokenData);
    sessionStorage.setItem('mfu_token', tokenData);
    sessionStorage.setItem('mfu_user', JSON.stringify(userData));
    sessionStorage.setItem('mfu_login_date', new Date().toDateString());
    sessionStorage.setItem('mfu_otp_verified', 'true');
    return true;
  };

  // Login with token (auto-login from URL)
  const loginWithToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString
      });

      if (response.data.status === 'success' && response.data.data) {
        const userData = response.data.data.user;

        setUser(userData);
        setToken(tokenString);

        sessionStorage.setItem('mfu_token', tokenString);
        sessionStorage.setItem('mfu_user', JSON.stringify(userData));
        sessionStorage.setItem('mfu_login_date', new Date().toDateString());
        sessionStorage.setItem('mfu_otp_verified', 'true');

        console.log('✅ Login with token success (Admin), user role:', userData.role);
        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Invalid token' };
      }
    } catch (error) {
      console.error('Token login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Token ไม่ถูกต้องหรือหมดอายุ'
      };
    }
  };

  // Verify token validity
  const verifyToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString || token
      });

      if (response.data.status !== 'success') {
        throw new Error('Token is invalid');
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  // Logout
  const logout = () => {
    setUser(null);
    setToken(null);
    clearStoredAuth();
    sessionStorage.removeItem('otp_pending');
    sessionStorage.removeItem('otp_verification_token');
  };

  // Get user profile
  const getProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 'success') {
        const userData = response.data.data.user;
        setUser(userData);
        sessionStorage.setItem('mfu_user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Get profile error:', error);
      return null;
    }
  };

  // Get current user's full name
  const getUserFullName = () => {
    if (!user) return 'ไม่ระบุ';

    if (user.fullName) return user.fullName;

    const parts = [];
    if (user.nameprefix) parts.push(user.nameprefix);
    if (user.name) parts.push(user.name);
    if (user.lastname) parts.push(user.lastname);

    return parts.length > 0 ? parts.join(' ') : (user.username || 'ไม่ระบุ');
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    completeOtpLogin,
    loginWithToken,
    logout,
    getProfile,
    getUserFullName
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
